import { useEffect, useRef, useState } from "react";
import { LucideIcon, Users, Wallet } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/i18n/useLanguage";

export interface ServicePackage {
  name: string;
  tagline?: string;
  price: string;
  items: string[];
}

export interface ServiceSectionData {
  id: string;
  index: number;
  eyebrowLabel: string;
  title: string;
  icon: LucideIcon;
  audience: string;
  intro: string;
  serviceDescription: string;
  explorationPoints: string[];
  deliverablesHeading: string;
  process: string;
  result: string;
  pricing?: string;
  packages?: ServicePackage[];
}

interface ServiceSectionProps {
  service: ServiceSectionData;
}

const ServiceSection = ({ service }: ServiceSectionProps) => {
  const { t } = useTranslation();
  const { localize } = useLanguage();
  const Icon = service.icon;
  const indexLabel = String(service.index).padStart(2, "0");
  const ref = useRef<HTMLElement>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setRevealed(true);
            observer.disconnect();
            break;
          }
        }
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.05 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      id={service.id}
      className={cn(
        "scroll-mt-28 py-16 lg:py-24 border-t border-border/40 first:border-t-0 first:pt-0",
        "transition-all duration-700 ease-out",
        revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      )}
    >
      <div className="max-w-3xl">
        {/* Header */}
        <header className="mb-10">
          <div className="flex items-center gap-3 mb-5">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-brand shadow-glow-primary">
              <Icon className="w-5 h-5 text-primary-foreground" />
            </div>
            <p className="text-sm font-semibold tracking-[0.2em] text-primary uppercase">
              {indexLabel}|{service.eyebrowLabel}
            </p>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground leading-[1.1] tracking-tight">
            {service.title}
          </h2>
          <div className="h-1 w-20 rounded-full bg-gradient-brand mt-5" />
        </header>

        {/* Audience + Pricing info boxes */}
        <div className="mb-8 grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-3 rounded-xl border border-primary/25 bg-primary/5 px-4 py-3">
            <div className="flex items-start gap-3">
              <Users className="w-4 h-4 text-primary mt-0.5 shrink-0" />
              <div className="flex flex-col">
                <span className="text-[11px] font-semibold tracking-[0.18em] text-primary uppercase">
                  {t("servicesPage.ui.audienceLabel")}
                </span>
                <span className="text-sm lg:text-base text-foreground/90 leading-snug">
                  {service.audience}
                </span>
              </div>
            </div>
            <div className="mt-auto pt-1">
              <Button asChild size="sm" variant="secondary" className="w-full sm:w-auto">
                <Link to={localize("/contact")}>{t("servicesPage.ui.audienceCta")}</Link>
              </Button>
            </div>
          </div>

          {service.pricing && (
            <div className="flex flex-col gap-3 rounded-xl border border-primary/25 bg-primary/5 px-4 py-3">
              <div className="flex items-start gap-3">
                <Wallet className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <div className="flex flex-col">
                  <span className="text-[11px] font-semibold tracking-[0.18em] text-primary uppercase">
                    {t("servicesPage.ui.pricingLabel")}
                  </span>
                  <span className="text-sm lg:text-base text-foreground/90 leading-snug">
                    {service.pricing}
                  </span>
                  {service.packages && service.packages.length > 0 && (
                    <Dialog>
                      <DialogTrigger asChild>
                        <button
                          type="button"
                          className="mt-2 text-left text-sm font-medium text-primary hover:underline underline-offset-4"
                        >
                          {t("servicesPage.ui.viewPackagesCta")}
                        </button>
                      </DialogTrigger>
                      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>{t("servicesPage.packagesDialog.title")}</DialogTitle>
                          <DialogDescription>
                            {t("servicesPage.packagesDialog.lead")}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="grid gap-4 mt-2 md:grid-cols-3">
                          {service.packages.map((pkg) => (
                            <div
                              key={pkg.name}
                              className="flex flex-col rounded-xl border border-border bg-card p-5"
                            >
                              <p className="text-base font-semibold text-foreground">
                                {pkg.name}
                              </p>
                              {pkg.tagline && (
                                <p className="text-sm text-muted-foreground mt-1">
                                  {pkg.tagline}
                                </p>
                              )}
                              <ul className="space-y-2 mt-4 mb-5">
                                {pkg.items.map((it, i) => (
                                  <li key={i} className="flex items-start gap-2">
                                    <span
                                      aria-hidden
                                      className="mt-2 inline-block w-1.5 h-1.5 rounded-full bg-gradient-brand shrink-0"
                                    />
                                    <span className="text-sm text-muted-foreground leading-relaxed">
                                      {it}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                              <div className="mt-auto pt-3 border-t border-border/60">
                                <p className="text-[11px] font-semibold tracking-[0.18em] text-primary uppercase">
                                  {t("servicesPage.packagesDialog.investmentLabel")}
                                </p>
                                <p className="text-lg font-semibold text-foreground mt-1">
                                  {pkg.price}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-muted-foreground mt-4">
                          {t("servicesPage.packagesDialog.priceNote")}
                        </p>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </div>
              <div className="mt-auto pt-1">
                <Button asChild size="sm" className="w-full sm:w-auto">
                  <Link to={localize("/contact")}>{t("servicesPage.ui.pricingCta")}</Link>
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Intro pull-quote */}
        <p className="text-lg lg:text-xl text-foreground/95 leading-relaxed border-l-2 border-primary/60 pl-5 mb-8">
          {service.intro}
        </p>

        <div className="space-y-7">
          <p className="text-base lg:text-lg text-muted-foreground leading-relaxed">
            {service.serviceDescription}
          </p>

          <div>
            <p className="text-base font-semibold mb-4 text-foreground">
              {service.deliverablesHeading}
            </p>
            <ul className="space-y-3">
              {service.explorationPoints.map((point, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <span
                    aria-hidden
                    className="mt-2.5 inline-block w-1.5 h-1.5 rounded-full bg-gradient-brand shrink-0"
                  />
                  <span className="text-base lg:text-lg text-muted-foreground leading-relaxed">
                    {point}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <p className="text-base lg:text-lg text-muted-foreground leading-relaxed">
            {service.process}
          </p>

          {/* Result callout */}
          <div className="rounded-xl border border-primary/30 bg-primary/5 p-5 lg:p-6">
            <p className="text-[11px] font-semibold tracking-[0.2em] text-primary uppercase mb-2">
              {t("servicesPage.ui.outcomeLabel")}
            </p>
            <p className="text-base lg:text-lg font-medium text-foreground leading-relaxed">
              {service.result}
            </p>
          </div>

          <div className="pt-2">
            <Button asChild size="lg">
              <Link to={localize("/contact")}>{t("servicesPage.ui.cta")}</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceSection;
